import { BookOpen, Clock, Play, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuthStore } from "@/features/auth/store";

import { useStartTour } from "../hooks";
import { useHelpStore } from "../store";

/**
 * Première connexion d'un utilisateur sur ce poste : trois portes de sortie,
 * toutes bonnes — la visite, le guide, ou plus tard. Ne s'affiche jamais en
 * session support / usurpation (le staff connaît l'application) ni sans
 * clinique active (l'écran de choix de clinique passe avant).
 */
export function WelcomeDialog() {
  const me = useAuthStore((s) => s.me);
  const userKey = useHelpStore((s) => s.userKey);
  const welcomeSeen = useHelpStore((s) => s.welcomeSeen);
  const markSeen = useHelpStore((s) => s.markWelcomeSeen);
  const startTour = useStartTour();
  const navigate = useNavigate();

  const eligible =
    !!me &&
    userKey === me.id &&
    !welcomeSeen &&
    !!me.active_organisation_id &&
    !me.is_platform_staff &&
    !me.is_impersonating &&
    !me.support_by &&
    !me.must_change_password;
  if (!eligible) return null;

  const firstName = me.full_name.split(" ")[0] ?? "";

  return (
    <Dialog open onOpenChange={(o) => !o && markSeen()}>
      <DialogContent className="max-w-xl" onEscapeKeyDown={markSeen}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" aria-hidden /> Bienvenue sur MADA{firstName ? `, ${firstName}` : ""}
          </DialogTitle>
          <DialogDescription className="text-primary-foreground/85">
            Deux minutes suffisent pour savoir où tout se trouve.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Des bulles d'aide marquées d'un point clignotant expliquent chaque écran. Le menu
            Aide, en haut à droite, reste disponible à tout moment.
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => startTour("decouverte")}
              className="flex cursor-pointer flex-col gap-1 border border-primary bg-primary/5 p-4 text-left transition-colors hover:bg-primary/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <span className="flex items-center gap-2 text-sm font-semibold text-primary">
                <Play className="h-4 w-4" aria-hidden /> Lancer la visite guidée
              </span>
              <span className="flex items-center gap-1 text-xs text-muted">
                <Clock className="h-3 w-3" aria-hidden /> 2 minutes, pas à pas
              </span>
            </button>
            <button
              type="button"
              onClick={() => {
                markSeen();
                navigate("/aide");
              }}
              className="flex cursor-pointer flex-col gap-1 border border-border p-4 text-left transition-colors hover:border-primary/40 hover:bg-primary/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
                <BookOpen className="h-4 w-4 text-primary" aria-hidden /> Lire le guide
              </span>
              <span className="text-xs text-muted">Tout le parcours, de l'inscription à la première facture</span>
            </button>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={markSeen}>
            Plus tard
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
